import * as React from 'react';

import { computeFloatingPosition } from './computeFloatingPosition';
import type { FloatingSide, FloatingAlign } from './computeFloatingPosition';
import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect';

export interface UseFloatingPositionOptions {
  /** Whether the floating element is currently visible. Position is only computed when true. */
  open: boolean;
  /** Which side of the trigger to place the content. @default 'bottom' */
  side?: FloatingSide;
  /** Alignment along the trigger's edge. @default 'start' */
  align?: FloatingAlign;
  /** Gap between trigger and content in px. @default 8 */
  offsetPx?: number;
  /** Auto-flip to opposite side when clipped. @default true */
  flip?: boolean;
  /**
   * Externally-managed ref for the trigger element.
   * When provided, the hook will use this ref instead of creating its own.
   * Useful when the trigger ref lives in a shared context (e.g. Dropdown, Filter).
   */
  triggerRef?: React.RefObject<HTMLElement | null>;
  /**
   * Externally-managed ref for the floating content element.
   * When provided, the hook will use this ref instead of creating its own.
   */
  floatingRef?: React.RefObject<HTMLElement | null>;
}

export interface UseFloatingPositionReturn {
  /** Ref to attach to the trigger element. */
  triggerRef: React.RefObject<HTMLElement | null>;
  /** Ref to attach to the floating content element. */
  floatingRef: React.RefObject<HTMLElement | null>;
  /** Computed { top, left } in px — apply as inline style with position: fixed. */
  coords: { top: number; left: number };
  /** Manually re-compute position (e.g. after content size changes). */
  updatePosition: () => void;
}

/**
 * Reusable hook for positioning a floating element relative to a trigger.
 *
 * Handles:
 * - Initial position calculation on open
 * - Automatic repositioning on scroll / resize
 * - Auto-flip when content would be clipped by the viewport
 * - Viewport edge clamping
 *
 * Usage (standalone — creates its own refs):
 * ```tsx
 * const { triggerRef, floatingRef, coords } = useFloatingPosition({
 *   open: isOpen,
 *   side: 'bottom',
 *   align: 'end',
 * });
 * ```
 *
 * Usage (with external refs — e.g. from a compound component context):
 * ```tsx
 * const { coords, updatePosition } = useFloatingPosition({
 *   open,
 *   side,
 *   align,
 *   triggerRef: ctx.triggerRef,
 *   floatingRef: ctx.contentRef,
 * });
 * ```
 */
export function useFloatingPosition({
  open,
  side = 'bottom',
  align = 'start',
  offsetPx = 8,
  flip = true,
  triggerRef: externalTriggerRef,
  floatingRef: externalFloatingRef,
}: UseFloatingPositionOptions): UseFloatingPositionReturn {
  const internalTriggerRef = React.useRef<HTMLElement>(null);
  const internalFloatingRef = React.useRef<HTMLElement>(null);

  const triggerRef = externalTriggerRef ?? internalTriggerRef;
  const floatingRef = externalFloatingRef ?? internalFloatingRef;

  const [coords, setCoords] = React.useState<{ top: number; left: number } | null>(null);

  const updatePosition = React.useCallback(() => {
    const trigger = triggerRef.current;
    const floating = floatingRef.current;
    if (!trigger || !floating) return;

    const triggerRect = trigger.getBoundingClientRect();
    const { width, height } = floating.getBoundingClientRect();

    const next = computeFloatingPosition({
      triggerRect,
      contentWidth: width || floating.offsetWidth,
      contentHeight: height || floating.offsetHeight,
      side,
      align,
      offsetPx,
      flip,
    });

    setCoords(next);
  }, [triggerRef, floatingRef, side, align, offsetPx, flip]);

  useIsomorphicLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return undefined;
    }

    // Position on the next frame — the floating element may not be in the DOM
    // yet (e.g. when Presence uses a double-RAF enter transition). A single
    // RAF is enough; the previous double-call (sync + RAF) caused a visible
    // bounce because the first calc ran with stale/zero dimensions.
    const raf = requestAnimationFrame(() => {
      updatePosition();
    });

    const onReposition = () => updatePosition();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open, updatePosition]);

  const resolvedCoords = coords ?? { top: -9999, left: -9999 };

  return { triggerRef, floatingRef, coords: resolvedCoords, updatePosition };
}
